import {
  AlignJustifyIcon,
  BriefcaseIcon,
  CleanIcon,
  CogIcon,
  EnvelopeIcon,
  GraphIcon,
  HomeIcon,
  LogInIcon,
  LogOutIcon,
  ManuallyEnteredDataIcon,
  SwapHorizontalIcon,
  TickCircleIcon,
  SearchTemplateIcon,
  UserIcon,
} from 'evergreen-ui';
import { path, tabs } from 'pages/routes';
import { AgentIcon, CircleQuestionIcon } from 'public/icons';
import * as companyHelpers from 'resources/company/company.helpers';
import { REALTORS, USER_TYPES } from 'resources/user/user.constants';

const { sideBarTabs } = tabs;

const sideBarTab = {
  [sideBarTabs.HOME]: {
    label: sideBarTabs.HOME,
    link: path.home,
    icon: () => <HomeIcon />,
  },
  [sideBarTabs.TEAMS]: {
    label: sideBarTabs.TEAMS,
    link: path.teams,
    icon: () => <AgentIcon />,
  },
  [sideBarTabs.TASKS]: {
    label: sideBarTabs.TASKS,
    link: path.tasks,
    icon: () => <TickCircleIcon />,
  },
  [sideBarTabs.AGENT_PROFILE]: {
    label: sideBarTabs.AGENT_PROFILE,
    link: path.agentProfile,
    icon: () => <AgentIcon />,
  },
  [sideBarTabs.TRANSACTIONS]: {
    label: sideBarTabs.TRANSACTIONS,
    link: path.transactions,
    icon: () => <SwapHorizontalIcon />,
  },
  [sideBarTabs.CLIENTS]: {
    label: sideBarTabs.CLIENTS,
    link: path.clients,
    tooltip: 'View Buyers and Sellers',
    icon: () => <CleanIcon />,
  },
  [sideBarTabs.MY_LISTEDKITS]: {
    label: sideBarTabs.MY_LISTEDKITS,
    link: path.listedKits,
    icon: () => <BriefcaseIcon />,
  },
  [sideBarTabs.EMAIL_TEMPLATES]: {
    label: sideBarTabs.EMAIL_TEMPLATES,
    link: path.emailTemplates,
    icon: () => <EnvelopeIcon />,
  },
  [sideBarTabs.E_SIGN_TEMPLATES]: {
    label: sideBarTabs.E_SIGN_TEMPLATES,
    link: path.eSignTemplates,
    icon: () => <ManuallyEnteredDataIcon />,
  },
  [sideBarTabs.SERVICE_PROVIDERS_LIST]: {
    label: sideBarTabs.SERVICE_PROVIDERS_LIST,
    link: path.serviceProvidersList,
    icon: () => <AlignJustifyIcon />,
  },
  [sideBarTabs.SETTINGS]: {
    label: sideBarTabs.SETTINGS,
    link: path.account,
    icon: () => <CogIcon />,
    isOnBottom: true,
  },
  [sideBarTabs.USERS]: {
    label: sideBarTabs.USERS,
    link: path.users,
    icon: () => <UserIcon />,
    isOnBottom: true,
  },
  [sideBarTabs.ADVANCED_FIELDS]: {
    label: sideBarTabs.ADVANCED_FIELDS,
    link: path.advancedFields,
    icon: () => <SearchTemplateIcon />,
    isOnBottom: true,
  },
  [sideBarTabs.INTEGRATIONS]: {
    label: sideBarTabs.INTEGRATIONS,
    link: path.integrations,
    icon: () => <GraphIcon />,
    isOnBottom: true,
  },
  [sideBarTabs.HELP_CENTER]: {
    label: sideBarTabs.HELP_CENTER,
    link: 'https://help.listedkit.com/',
    icon: () => <CircleQuestionIcon />,
    isOnBottom: true,
  },
  [sideBarTabs.LOG_OUT]: {
    label: tabs.sideBarTabs.LOG_OUT,
    icon: () => <LogOutIcon />,
    isOnBottom: true,
  },
  [sideBarTabs.LOG_IN]: {
    label: tabs.sideBarTabs.LOG_IN,
    icon: () => <LogInIcon />,
    isOnBottom: true,
  },
};

const getCorporateRealtorSideBarTabs = (isESignatureEnabled = false) => [
  sideBarTabs.HOME,
  sideBarTabs.TASKS,
  sideBarTabs.TRANSACTIONS,
  sideBarTabs.CLIENTS,
  sideBarTabs.MY_LISTEDKITS,
  sideBarTabs.EMAIL_TEMPLATES,
  ...(isESignatureEnabled ? [sideBarTabs.E_SIGN_TEMPLATES] : []),
  sideBarTabs.SERVICE_PROVIDERS_LIST,
  sideBarTabs.SETTINGS,
  sideBarTabs.ADVANCED_FIELDS,
  sideBarTabs.INTEGRATIONS,
  sideBarTabs.HELP_CENTER,
  sideBarTabs.LOG_OUT,
];

const getPersonalRealtorSideBarTabs = (isESignatureEnabled = false) => [
  sideBarTabs.HOME,
  sideBarTabs.TASKS,
  sideBarTabs.TRANSACTIONS,
  sideBarTabs.CLIENTS,
  sideBarTabs.MY_LISTEDKITS,
  sideBarTabs.EMAIL_TEMPLATES,
  ...(isESignatureEnabled ? [sideBarTabs.E_SIGN_TEMPLATES] : []),
  sideBarTabs.SERVICE_PROVIDERS_LIST,
  sideBarTabs.SETTINGS,
  sideBarTabs.ADVANCED_FIELDS,
  sideBarTabs.INTEGRATIONS,
  sideBarTabs.HELP_CENTER,
  sideBarTabs.LOG_OUT,
];

const getClientSideBarTabs = () => [
  sideBarTabs.HOME,
  sideBarTabs.TASKS,
  sideBarTabs.AGENT_PROFILE,
  sideBarTabs.SERVICE_PROVIDERS_LIST,
  sideBarTabs.LOG_OUT,
];

const getCoordinatorSideBarTabs = (isESignatureEnabled = false) => [
  sideBarTabs.HOME,
  sideBarTabs.TEAMS,
  sideBarTabs.TASKS,
  sideBarTabs.TRANSACTIONS,
  sideBarTabs.CLIENTS,
  sideBarTabs.MY_LISTEDKITS,
  sideBarTabs.EMAIL_TEMPLATES,
  ...(isESignatureEnabled ? [sideBarTabs.E_SIGN_TEMPLATES] : []),
  sideBarTabs.SERVICE_PROVIDERS_LIST,
  sideBarTabs.SETTINGS,
  sideBarTabs.USERS,
  sideBarTabs.ADVANCED_FIELDS,
  sideBarTabs.INTEGRATIONS,
  sideBarTabs.HELP_CENTER,
  sideBarTabs.LOG_OUT,
];

const getAdminSideBarTabs = (isESignatureEnabled = false) => [
  sideBarTabs.HOME,
  sideBarTabs.TEAMS,
  sideBarTabs.TASKS,
  sideBarTabs.TRANSACTIONS,
  sideBarTabs.CLIENTS,
  sideBarTabs.MY_LISTEDKITS,
  sideBarTabs.EMAIL_TEMPLATES,
  ...(isESignatureEnabled ? [sideBarTabs.E_SIGN_TEMPLATES] : []),
  sideBarTabs.SERVICE_PROVIDERS_LIST,
  sideBarTabs.SETTINGS,
  sideBarTabs.USERS,
  sideBarTabs.ADVANCED_FIELDS,
  sideBarTabs.INTEGRATIONS,
  sideBarTabs.HELP_CENTER,
  sideBarTabs.LOG_OUT,
];

const getMarketingSideBarTabs = () => [
  sideBarTabs.HOME,
  sideBarTabs.TEAMS,
  sideBarTabs.TASKS,
  sideBarTabs.TRANSACTIONS,
  sideBarTabs.CLIENTS,
  sideBarTabs.MY_LISTEDKITS,
  sideBarTabs.EMAIL_TEMPLATES,
  sideBarTabs.SERVICE_PROVIDERS_LIST,
  sideBarTabs.LOG_IN,
];

const getUserTabs = (user, company) => {
  const isESignatureEnabled = companyHelpers.isEsignatureEnabled(company);

  if (user?.type === USER_TYPES.REALTOR) {
    return companyHelpers.isPersonal(company)
      ? getPersonalRealtorSideBarTabs(isESignatureEnabled)
      : getCorporateRealtorSideBarTabs(isESignatureEnabled);
  }

  switch (user?.type) {
    case REALTORS.CORPORATE_REALTOR:
      return getCorporateRealtorSideBarTabs(isESignatureEnabled);
    case REALTORS.PERSONAL_REALTOR:
      return getPersonalRealtorSideBarTabs(isESignatureEnabled);
    case USER_TYPES.COORDINATOR:
      return getCoordinatorSideBarTabs(isESignatureEnabled);
    case USER_TYPES.ADMIN:
      return getAdminSideBarTabs(isESignatureEnabled);
    case USER_TYPES.CLIENT:
      return getClientSideBarTabs();
    default:
      return getMarketingSideBarTabs();
  }
};

const getSideBarTabs = (user, company) => {
  const userTabs = getUserTabs(user, company);

  return userTabs.map((tabLabel) => sideBarTab[tabLabel]);
};

export default getSideBarTabs;
